import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SmsHubLayout } from "@/components/sms/SmsHubLayout";
import { listTwilioNumbers, upsertTwilioNumber, deleteTwilioNumber, twilioCredsStatus } from "@/server/sms.functions";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/dashboard/admin/sms/twilio")({
  head: () => ({ meta: [{ title: "Twilio · SMS" }, { name: "robots", content: "noindex" }] }),
  component: Page,
});

function Page() {
  const { primaryRole } = useAuth();
  const qc = useQueryClient();
  const list = useServerFn(listTwilioNumbers);
  const up = useServerFn(upsertTwilioNumber);
  const del = useServerFn(deleteTwilioNumber);
  const creds = useServerFn(twilioCredsStatus);

  const { data, isLoading } = useQuery({ queryKey: ["sms-numbers"], queryFn: () => list(), enabled: primaryRole === "admin" });
  const { data: status } = useQuery({ queryKey: ["sms-twilio-creds"], queryFn: () => creds(), enabled: primaryRole === "admin" });

  const [open, setOpen] = useState(false);
  const [e164, setE164] = useState("+1");
  const [friendly, setFriendly] = useState("");
  const [cap, setCap] = useState(500);

  const addMut = useMutation({
    mutationFn: () => up({ data: { e164, friendly_name: friendly, daily_cap: cap, enabled: true } }),
    onSuccess: () => { toast.success("Number added"); setOpen(false); setE164("+1"); setFriendly(""); qc.invalidateQueries({ queryKey: ["sms-numbers"] }); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });
  const toggleMut = useMutation({
    mutationFn: (n: any) => up({ data: { id: n.id, e164: n.e164, enabled: !n.enabled } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms-numbers"] }),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms-numbers"] }),
  });

  const s: any = status ?? {};

  return (
    <SmsHubLayout title="Twilio Settings">
      <div className="border border-border rounded-md bg-card p-4 mb-4">
        <h3 className="text-[13px] font-semibold mb-3">Credential status</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
          <CredBadge label="Account SID" set={s.account_sid_set} />
          <CredBadge label="Auth Token" set={s.auth_token_set} />
          <CredBadge label="Messaging Service SID" set={s.messaging_service_sid_set} />
          <CredBadge label="From number" set={s.from_number_set} />
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          Twilio credentials are stored as backend secrets (never exposed to the browser). To update them, ask the admin to manage <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, <code>TWILIO_MESSAGING_SERVICE_SID</code>, <code>TWILIO_FROM_NUMBER</code> in backend secrets.
        </p>
        <p className="text-[11px] mt-2">
          <strong>Live mode:</strong> <span className={s.live_mode_enabled ? "text-emerald-700" : "text-amber-700"}>{s.live_mode_enabled ? "ENABLED" : "DISABLED (Phase 1)"}</span>
        </p>
      </div>

      <div className="flex justify-between items-center mb-3">
        <h3 className="text-[13px] font-semibold">Sending numbers ({(data ?? []).length} / 10)</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" disabled={(data ?? []).length >= 10}><Plus className="h-3.5 w-3.5 mr-1.5" />Add number</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Twilio number</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Phone (E.164)</Label><Input value={e164} onChange={(e) => setE164(e.target.value)} placeholder="+15551234567" /></div>
              <div><Label>Friendly name</Label><Input value={friendly} onChange={(e) => setFriendly(e.target.value)} /></div>
              <div><Label>Daily cap</Label><Input type="number" value={cap} onChange={(e) => setCap(parseInt(e.target.value) || 0)} /></div>
              <Button onClick={() => addMut.mutate()} disabled={addMut.isPending || !/^\+\d{8,15}$/.test(e164)} className="w-full">Add</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <BulkAddNineNumbers
        existingCount={(data ?? []).length}
        onAdd={async (numbers) => {
          let added = 0;
          for (const n of numbers) {
            try {
              await up({ data: { e164: n, friendly_name: "", daily_cap: 500, enabled: true } });
              added++;
            } catch (e: any) {
              toast.error(`${n}: ${e.message ?? "failed"}`);
            }
          }
          if (added > 0) {
            toast.success(`Added ${added} number${added === 1 ? "" : "s"}`);
            qc.invalidateQueries({ queryKey: ["sms-numbers"] });
          }
        }}
      />

      <div className="border border-border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Phone</TableHead>
              <TableHead>Friendly name</TableHead>
              <TableHead>Daily cap</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={5} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>}
            {!isLoading && (data ?? []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Add up to 10 Twilio numbers.</TableCell></TableRow>}
            {(data ?? []).map((n: any) => (
              <TableRow key={n.id}>
                <TableCell className="font-mono text-[12.5px]">{n.e164}</TableCell>
                <TableCell className="text-[12.5px]">{n.friendly_name ?? "—"}</TableCell>
                <TableCell className="text-[12.5px]">{n.daily_cap}</TableCell>
                <TableCell><Switch checked={n.enabled} onCheckedChange={() => toggleMut.mutate(n)} /></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => delMut.mutate(n.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </SmsHubLayout>
  );
}

function CredBadge({ label, set }: { label: string; set: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`h-2 w-2 rounded-full ${set ? "bg-emerald-500" : "bg-rose-500"}`} />
      <span>{label}: <strong>{set ? "configured" : "missing"}</strong></span>
    </div>
  );
}

function BulkAddNineNumbers({ existingCount, onAdd }: { existingCount: number; onAdd: (numbers: string[]) => Promise<void> }) {
  const remaining = Math.max(0, 10 - existingCount);
  const slots = Math.min(9, remaining);
  const [values, setValues] = useState<string[]>(() => Array(9).fill(""));
  const [busy, setBusy] = useState(false);

  if (slots === 0) return null;

  const setAt = (i: number, v: string) => setValues((prev) => prev.map((x, idx) => (idx === i ? v : x)));
  const valid = (v: string) => /^\+\d{8,15}$/.test(v.trim());

  const submit = async () => {
    const cleaned = values.slice(0, slots).map((v) => v.trim()).filter(Boolean);
    const bad = cleaned.filter((v) => !valid(v));
    if (bad.length) { toast.error(`Invalid E.164: ${bad.join(", ")}`); return; }
    if (cleaned.length === 0) { toast.error("Enter at least one number"); return; }
    setBusy(true);
    try { await onAdd(cleaned); setValues(Array(9).fill("")); } finally { setBusy(false); }
  };

  return (
    <div className="border border-border rounded-md bg-card p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[13px] font-semibold">Bulk add remaining numbers ({slots} slot{slots === 1 ? "" : "s"} available)</h3>
        <Button size="sm" onClick={submit} disabled={busy}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
          Add all
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {Array.from({ length: slots }).map((_, i) => (
          <div key={i}>
            <Label className="text-[11px] text-muted-foreground">Number {i + 2}</Label>
            <Input
              value={values[i]}
              onChange={(e) => setAt(i, e.target.value)}
              placeholder="+16095551234"
              className="font-mono text-[12.5px]"
            />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">
        E.164 format (e.g. <code>+16095551234</code>). Each will be added with a 500/day cap and enabled by default.
      </p>
    </div>
  );
}
